import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import Confetti from "react-confetti";

import {
  TileShape,
  ShadowShape,
} from "@/features/DragonTiles/components/ParentTile/ParentTile.styles";

interface SuccessAnimationProps {
  isVisible: boolean;
  onComplete?: () => void;
}

// Custom styled components for the success animation
const SuccessTileWrapper = styled("div")({
  position: "relative",
  width: "700px",
  height: "800px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const SuccessTileContent = styled(motion.div)({
  position: "absolute",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 20px",
  zIndex: 2,
});

const SuccessTitle = styled(motion.div)({
  fontSize: "34px",
  fontWeight: 600,
  color: "#7700EE",
  textTransform: "uppercase",
  letterSpacing: "1px",
  textAlign: "center",
  marginTop: "48px",
});

const SuccessMessage = styled(motion.div)({
  fontSize: "64px",
  fontWeight: 600,
  color: "#2D3748",
  textAlign: "center",
  lineHeight: "1.2",
});

export function SuccessAnimation({
  isVisible,
  onComplete,
}: SuccessAnimationProps) {
  useEffect(() => {
    if (isVisible && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 3000); // Show animation for 3 seconds

      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            numberOfPieces={400}
            colors={[
              "#7700EE", // Purple
              "#F4289D", // Pink
              "#FBF2CA", // Yellow
              "#E7FFFA", // Light green
              "#F5F2F8", // Light purple
            ]}
            recycle={false}
            gravity={0.5}
            wind={0.1}
          />
          <motion.div
            initial={{ scale: 0.3, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.3, opacity: 0, y: 50 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              delay: 0.1,
            }}
          >
            <SuccessTileWrapper>
              <TileShape bgColor="#F5F2F8" variant="extraLarge" />
              <ShadowShape variant="extraLarge" />
              <SuccessTileContent
                initial={{ rotate: -5, scale: 0.8 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  delay: 0.2,
                }}
              >
                <SuccessTitle
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.6,
                    duration: 0.5,
                  }}
                >
                  Broadcast
                </SuccessTitle>
                <SuccessMessage
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.8,
                    duration: 0.5,
                  }}
                >
                  Successfully
                  <br />
                  confirmed!
                </SuccessMessage>
              </SuccessTileContent>
            </SuccessTileWrapper>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
}
